/**
 * SettingsSection Component
 * Reusable CRUD section for resource types, tags, and authors settings
 */

import { useState } from 'react';
import { Pencil, Plus, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';

type SectionItem = {
  id: string;
  name: string;
  isActive?: boolean;
  _count?: { resources: number };
};

type SettingsSectionProps = {
  title: string;
  items: SectionItem[];
  isLoading: boolean;
  placeholderText: string;
  onCreate: (payload: { name: string }) => Promise<unknown>;
  onUpdate: (payload: { id: string; name: string }) => Promise<unknown>;
  onAction: (payload: { id: string }) => void;
  actionType: 'deactivate' | 'delete';
  createPending?: boolean;
};

export function SettingsSection({
  title,
  items,
  isLoading,
  placeholderText,
  onCreate,
  onUpdate,
  onAction,
  actionType,
  createPending = false,
}: SettingsSectionProps) {
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const handleAdd = async () => {
    if (!addName.trim()) return;
    await onCreate({ name: addName.trim() });
    setAddName('');
    setShowAdd(false);
  };

  const handleStartEdit = (item: SectionItem) => {
    setEditingId(item.id);
    setEditName(item.name);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    await onUpdate({ id: editingId, name: editName.trim() });
    setEditingId(null);
    setEditName('');
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-32" />
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button
          type="button"
          aria-label={`Add ${title.toLowerCase()}`}
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[#026857] text-white rounded-md hover:bg-[#024d43] transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Add
        </button>
      </div>

      {showAdd && (
        <div className="flex items-center gap-2 p-3 border border-gray-200 rounded-md bg-gray-50">
          <input
            type="text"
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            placeholder={placeholderText}
            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-[#026857] focus:ring-1 focus:ring-[#026857]"
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={createPending || !addName.trim()}
            className="px-3 py-1.5 text-sm bg-[#026857] text-white rounded-md hover:bg-[#024d43] disabled:opacity-50 transition-colors"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => { setShowAdd(false); setAddName(''); }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="divide-y divide-gray-100">
        {items.map((item) => (
          <div
            key={item.id}
            data-testid={`settings-item-${item.id}`}
            className="flex items-center justify-between py-2.5 px-1"
          >
            {editingId === item.id ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-[#026857] focus:ring-1 focus:ring-[#026857]"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveEdit()}
                />
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className="px-3 py-1.5 text-sm bg-[#026857] text-white rounded-md hover:bg-[#024d43] transition-colors"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditingId(null)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {item.name}
                  </span>
                  {item.isActive === false && (
                    <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
                      Inactive
                    </span>
                  )}
                  {item._count && (
                    <span className="text-xs text-gray-400">
                      ({item._count.resources} resources)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label="Edit"
                    onClick={() => handleStartEdit(item)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {actionType === 'deactivate' ? (
                    <button
                      type="button"
                      aria-label="Toggle"
                      onClick={() => onAction({ id: item.id })}
                      className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {item.isActive ? (
                        <ToggleRight className="h-4 w-4 text-[#026857]" />
                      ) : (
                        <ToggleLeft className="h-4 w-4" />
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      aria-label="Delete"
                      onClick={() => onAction({ id: item.id })}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}

        {items.length === 0 && (
          <p className="py-4 text-center text-sm text-gray-400">
            No {title.toLowerCase()} found
          </p>
        )}
      </div>
    </div>
  );
}
